import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus, Trash2, Edit, Search, UserCheck, UserX, Key, RotateCcw, Copy,
  Eye, ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle, X, Users as UsersIcon,
} from 'lucide-react';
import { useTheme } from '../../Context/ThemeContext';
import { useRole } from '../../Context/RoleContext';
import { AuthApi } from '../../Api/BaseApi';
import './index.css';

// The form offers Admin / User (User maps to "AR Analyst" on the backend).
const emptyNewUser = {
  email: '', firstName: '', lastName: '', role: 'user',
  passwordMode: 'generate',          // 'generate' | 'manual'
  password: '', mustChangePassword: true,
  accessStartAt: '', accessEndAt: '',
  queryLimitType: 'unlimited', queryLimit: 100,
  notes: '',
};

const isAdminRole = (role) => String(role).toLowerCase() === 'admin';
const statusOf = (u) => String(u.status || (u.is_active ? 'active' : 'suspended')).toLowerCase();

const STATUS_LABEL = { active: 'Active', suspended: 'Suspended', pending: 'Pending', expired: 'Expired' };

const StatusBadge = ({ status }) => {
  const s = String(status || 'active').toLowerCase();
  return <span className={`status-badge ${s}`}>{STATUS_LABEL[s] || s}</span>;
};

const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
const accessWindowLabel = (u) => {
  if (!u.access_start_at && !u.access_end_at) return 'No restriction';
  return `${fmtDate(u.access_start_at)} → ${fmtDate(u.access_end_at)}`;
};

const UserManagement = () => {
  const { isDarkMode } = useTheme();
  const { isAdmin } = useRole();
  const [users, setUsers] = useState([]);
  const [me, setMe] = useState(null);          // current admin (for self-edit warnings)
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [detailUser, setDetailUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordChangeUser, setPasswordChangeUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [generatedInfo, setGeneratedInfo] = useState(null);   // secure reveal modal
  const [confirm, setConfirm] = useState(null);               // { title, message, danger, onConfirm }
  const [newUser, setNewUser] = useState(emptyNewUser);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  useEffect(() => {
    if (!error && !success) return;
    const timer = setTimeout(() => { setError(''); setSuccess(''); }, 4000);
    return () => clearTimeout(timer);
  }, [error, success]);

  useEffect(() => {
    if (isAdmin()) {
      loadUsers();
      AuthApi.getUserPermissions().then(setMe).catch(() => {});
    }
  }, [isAdmin]);

  const loadUsers = async () => {
    setLoading(true); setError('');
    try {
      const response = await AuthApi.getUsers({ size: 500 });
      setUsers(response.users || response.data || response);
    } catch (e) {
      setError('Failed to load users. Please try again.');
      setUsers([]);
    } finally { setLoading(false); }
  };

  const isSelf = (u) => me && u && (u.id === me.id || u.email === me.email);

  // --- Summary stats -------------------------------------------------------
  const stats = useMemo(() => {
    const s = { total: users.length, active: 0, suspended: 0, expired: 0, pending: 0, admin: 0 };
    users.forEach((u) => {
      const st = statusOf(u);
      if (s[st] !== undefined) s[st] += 1;
      if (isAdminRole(u.role)) s.admin += 1;
    });
    return s;
  }, [users]);

  const buildControlFields = (src) => {
    const payload = {
      access_start_at: src.accessStartAt ? new Date(src.accessStartAt).toISOString() : null,
      access_end_at: src.accessEndAt ? new Date(src.accessEndAt).toISOString() : null,
      query_limit_type: src.queryLimitType,
      notes: src.notes || '',
    };
    if (src.queryLimitType === 'limited') payload.query_limit = Number(src.queryLimit) || 0;
    return payload;
  };

  // --- Create --------------------------------------------------------------
  const addValid = newUser.firstName.trim() && newUser.lastName.trim()
    && /\S+@\S+\.\S+/.test(newUser.email)
    && (newUser.passwordMode === 'generate' || newUser.password.length >= 8);

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!addValid) return;
    setLoading(true); setError(''); setSuccess('');
    try {
      const userData = {
        email: newUser.email, first_name: newUser.firstName, last_name: newUser.lastName,
        role: newUser.role, must_change_password: newUser.mustChangePassword,
        ...buildControlFields(newUser),
      };
      if (newUser.passwordMode === 'generate') userData.generate_password = true;
      else userData.password = newUser.password;
      const created = await AuthApi.createUser(userData);
      setShowAddModal(false);
      setNewUser(emptyNewUser);
      if (created.generated_password) {
        setGeneratedInfo({ email: created.email, password: created.generated_password,
                           emailSent: created.welcome_email_sent, title: 'User created' });
      } else {
        setSuccess(`User ${created.email} created successfully.`);
      }
      await loadUsers();
    } catch (err) {
      const data = err.response?.data;
      setError((data?.errors && Object.values(data.errors)[0]) || data?.detail || 'Failed to create user.');
    } finally { setLoading(false); }
  };

  // --- Edit ----------------------------------------------------------------
  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!editingUser.first_name?.trim() || !editingUser.last_name?.trim()) {
      setError('First and last name are required.'); return;
    }
    setLoading(true); setError(''); setSuccess('');
    try {
      const userData = {
        first_name: editingUser.first_name, last_name: editingUser.last_name,
        role: editingUser.role, notes: editingUser.notes || '',
        access_start_at: editingUser.accessStartAt ? new Date(editingUser.accessStartAt).toISOString() : null,
        access_end_at: editingUser.accessEndAt ? new Date(editingUser.accessEndAt).toISOString() : null,
        query_limit_type: editingUser.query_limit_type,
      };
      if (editingUser.query_limit_type === 'limited') userData.query_limit = Number(editingUser.query_limit) || 0;
      await AuthApi.updateUser(editingUser.id, userData);
      setSuccess('User updated successfully.');
      setEditingUser(null);
      await loadUsers();
    } catch (err) {
      const data = err.response?.data;
      setError((data?.errors && Object.values(data.errors)[0]) || data?.detail || 'Failed to update user.');
    } finally { setLoading(false); }
  };

  // Detect a risky edit (shortening the access window of an active user) -> confirm.
  const submitEdit = (e) => {
    e.preventDefault();
    const orig = users.find((u) => u.id === editingUser.id) || {};
    const newEnd = editingUser.accessEndAt ? new Date(editingUser.accessEndAt) : null;
    const oldEnd = orig.access_end_at ? new Date(orig.access_end_at) : null;
    const shortening = newEnd && (!oldEnd || newEnd < oldEnd) && statusOf(orig) === 'active';
    const demotingSelf = isSelf(editingUser) && isAdminRole(orig.role) && editingUser.role === 'user';
    if (demotingSelf) {
      askConfirm({
        title: 'Remove your own admin access?', danger: true,
        message: 'You are about to remove your own admin role. You will lose access to this page immediately. Continue?',
        onConfirm: () => handleEditUser(e),
      });
    } else if (shortening) {
      askConfirm({
        title: 'Shorten access for an active user?',
        message: `This will set ${orig.email}'s access to end on ${fmtDate(editingUser.accessEndAt)}, which may immediately restrict their sign-in. Continue?`,
        onConfirm: () => handleEditUser(e),
      });
    } else {
      handleEditUser(e);
    }
  };

  // --- Destructive / risky actions (with confirmation) ---------------------
  const askConfirm = (cfg) => setConfirm(cfg);

  const doDelete = (user) => askConfirm({
    title: 'Delete user', danger: true,
    message: `Permanently delete ${user.email}? This cannot be undone.`,
    confirmLabel: 'Delete',
    onConfirm: async () => {
      setLoading(true); setError(''); setSuccess('');
      try {
        await AuthApi.deleteUser(user.id);
        setSuccess('User deleted successfully.');
        await loadUsers();
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to delete user.');
      } finally { setLoading(false); }
    },
  });

  const doToggleStatus = (user) => {
    const suspending = user.is_active;
    const run = async () => {
      setLoading(true); setError(''); setSuccess('');
      try {
        await AuthApi.toggleUserStatus(user.id);
        setSuccess(`User ${suspending ? 'suspended' : 'enabled'} successfully.`);
        await loadUsers();
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to update status.');
      } finally { setLoading(false); }
    };
    if (suspending) {
      askConfirm({
        title: 'Suspend user', danger: true,
        message: `Suspend ${user.email}? They will not be able to sign in until re-enabled.`,
        confirmLabel: 'Suspend', onConfirm: run,
      });
    } else { run(); }
  };

  const doResetUsage = async (user) => {
    setLoading(true); setError(''); setSuccess('');
    try {
      await AuthApi.resetUserUsage(user.id);
      setSuccess(`Query usage reset for ${user.email}.`);
      await loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset usage.');
    } finally { setLoading(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setSuccess('');
    try {
      const res = await AuthApi.changeUserPassword(passwordChangeUser.id, newPassword, { must_change_password: true });
      setShowPasswordModal(false); setPasswordChangeUser(null); setNewPassword('');
      if (res.generated_password) {
        setGeneratedInfo({ email: res.email, password: res.generated_password, title: 'Password reset' });
      } else {
        setSuccess(`Password reset for ${res.email}.`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password.');
    } finally { setLoading(false); }
  };

  const handleGeneratePassword = async (user) => {
    setLoading(true); setError(''); setSuccess('');
    try {
      const res = await AuthApi.changeUserPassword(user.id, null, { generate_password: true });
      setShowPasswordModal(false); setPasswordChangeUser(null); setNewPassword('');
      if (res.generated_password) setGeneratedInfo({ email: res.email, password: res.generated_password, title: 'Password reset' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate password.');
    } finally { setLoading(false); }
  };

  const openEdit = (user) => {
    setEditingUser({
      ...user,
      role: isAdminRole(user.role) ? 'Admin' : 'user',
      accessStartAt: toLocalInput(user.access_start_at),
      accessEndAt: toLocalInput(user.access_end_at),
      query_limit_type: user.query_limit_type || 'unlimited',
      query_limit: user.query_limit || 0,
      notes: user.notes || '',
    });
  };

  const openPasswordModal = (user) => {
    setPasswordChangeUser(user); setNewPassword(''); setShowPasswordModal(true);
    setError(''); setSuccess('');
  };

  // --- Filter + sort -------------------------------------------------------
  const toggleSort = (key) => setSort((s) =>
    s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  const SortHeader = ({ label, k }) => {
    const active = sort.key === k;
    const Icon = !active ? ArrowUpDown : (sort.dir === 'asc' ? ArrowUp : ArrowDown);
    return (
      <th>
        <button type="button" className={`th-sort${active ? ' active' : ''}`} onClick={() => toggleSort(k)}
                aria-label={`Sort by ${label}`}>
          {label} <Icon size={13} />
        </button>
      </th>
    );
  };

  const visibleUsers = useMemo(() => {
    const term = searchTerm.toLowerCase();
    let list = users.filter((u) => {
      const matchesSearch = !term || u.email?.toLowerCase().includes(term)
        || u.first_name?.toLowerCase().includes(term) || u.last_name?.toLowerCase().includes(term);
      const matchesRole = roleFilter === 'all' || (roleFilter === 'admin' ? isAdminRole(u.role) : !isAdminRole(u.role));
      const matchesStatus = statusFilter === 'all' || statusOf(u) === statusFilter;
      return matchesSearch && matchesRole && matchesStatus;
    });
    const val = (u) => {
      switch (sort.key) {
        case 'name': return `${u.first_name || ''} ${u.last_name || ''}`.trim().toLowerCase();
        case 'role': return isAdminRole(u.role) ? 0 : 1;
        case 'status': return statusOf(u);
        case 'access_end': return u.access_end_at ? new Date(u.access_end_at).getTime() : Infinity;
        case 'queries_used': return u.queries_used ?? 0;
        default: return '';
      }
    };
    list.sort((a, b) => {
      const av = val(a), bv = val(b);
      if (av < bv) return sort.dir === 'asc' ? -1 : 1;
      if (av > bv) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [users, searchTerm, roleFilter, statusFilter, sort]);

  const hasFilters = searchTerm || roleFilter !== 'all' || statusFilter !== 'all';

  if (!isAdmin()) {
    return (
      <div className="access-denied">
        <h2>Access Denied</h2>
        <p>You don't have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className={`user-management ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="user-management-header">
        <div>
          <h1>User Management</h1>
          <p className="um-subtitle">Create users, assign access, control query limits, and manage account validity.</p>
        </div>
        <button className="add-user-btn" onClick={() => { setNewUser(emptyNewUser); setShowAddModal(true); }} disabled={loading}>
          <Plus size={18} /> Add User
        </button>
      </div>

      {/* Summary cards */}
      <div className="um-stats">
        <div className="um-stat"><span className="um-stat-num">{stats.total}</span><span className="um-stat-label">Total users</span></div>
        <div className="um-stat active"><span className="um-stat-num">{stats.active}</span><span className="um-stat-label">Active</span></div>
        <div className="um-stat suspended"><span className="um-stat-num">{stats.suspended}</span><span className="um-stat-label">Suspended</span></div>
        <div className="um-stat expired"><span className="um-stat-num">{stats.expired}</span><span className="um-stat-label">Expired</span></div>
        <div className="um-stat admin"><span className="um-stat-num">{stats.admin}</span><span className="um-stat-label">Admins</span></div>
      </div>

      {error && <div className="alert alert-error" role="alert" aria-live="assertive">{error}</div>}
      {success && <div className="alert alert-success" role="status" aria-live="polite">{success}</div>}

      <div className="search-container">
        <div className="search-input-wrapper">
          <Search size={18} className="search-icon" />
          <input type="text" placeholder="Search by name or email..." value={searchTerm}
                 onChange={(e) => setSearchTerm(e.target.value)} className="search-input" aria-label="Search users" />
        </div>
        <select className="filter-select" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} aria-label="Filter by role">
          <option value="all">All roles</option><option value="admin">Admin</option><option value="user">User</option>
        </select>
        <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filter by status">
          <option value="all">All statuses</option><option value="active">Active</option>
          <option value="suspended">Suspended</option><option value="pending">Pending</option><option value="expired">Expired</option>
        </select>
      </div>

      <div className={`users-table-container${loading ? ' is-loading' : ''}`}>
        <table className="users-table">
          <thead>
            <tr>
              <SortHeader label="Name" k="name" />
              <th>Email</th>
              <SortHeader label="Role" k="role" />
              <SortHeader label="Status" k="status" />
              <SortHeader label="Access Window" k="access_end" />
              <th>Query Limit</th>
              <SortHeader label="Queries Used" k="queries_used" />
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && visibleUsers.map((user) => {
              const admin = isAdminRole(user.role);
              const limited = user.query_limit_type === 'limited';
              const exhausted = limited && (user.queries_used ?? 0) >= user.query_limit;
              return (
                <tr key={user.id} className={isSelf(user) ? 'is-self-row' : ''}>
                  <td>{`${user.first_name || ''} ${user.last_name || ''}`.trim() || '—'}{isSelf(user) && <span className="you-tag">You</span>}</td>
                  <td>{user.email}</td>
                  <td><span className={`role-badge ${admin ? 'admin' : 'user'}`}>{admin ? 'ADMIN' : 'USER'}</span></td>
                  <td><StatusBadge status={statusOf(user)} /></td>
                  <td className="nowrap">{accessWindowLabel(user)}</td>
                  <td>{limited ? user.query_limit : 'Unlimited'}</td>
                  <td className={exhausted ? 'usage-exhausted' : ''}>{user.queries_used ?? 0}{exhausted && ' (limit reached)'}</td>
                  <td>
                    <div className="action-buttons">
                      <button className="action-btn view" onClick={() => setDetailUser(user)} title="View details" aria-label={`View ${user.email}`}><Eye size={16} /></button>
                      <button className="action-btn edit" onClick={() => openEdit(user)} title="Edit user" aria-label={`Edit ${user.email}`}><Edit size={16} /></button>
                      <button className="action-btn password" onClick={() => openPasswordModal(user)} title="Reset password" aria-label={`Reset password for ${user.email}`}><Key size={16} /></button>
                      <button className="action-btn reset-usage" onClick={() => doResetUsage(user)} disabled={!limited} title="Reset query usage" aria-label={`Reset usage for ${user.email}`}><RotateCcw size={16} /></button>
                      <button className="action-btn toggle-status" onClick={() => doToggleStatus(user)} title={user.is_active ? 'Suspend user' : 'Enable user'} aria-label={`${user.is_active ? 'Suspend' : 'Enable'} ${user.email}`}>{user.is_active ? <UserX size={16} /> : <UserCheck size={16} />}</button>
                      <button className="action-btn delete" onClick={() => doDelete(user)} title="Delete user" aria-label={`Delete ${user.email}`}><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {loading && (
          <div className="loading-overlay" role="status" aria-live="polite">
            <div className="loading-card-table"><div className="loading-spinner-table" />
              <div className="loading-texts"><div className="loading-title">Loading users…</div>
                <div className="loading-subtitle">Please wait.</div></div></div>
          </div>
        )}

        {!loading && visibleUsers.length === 0 && (
          <div className="no-users">
            <UsersIcon size={32} className="no-users-icon" />
            {hasFilters ? (
              <>
                <p><strong>No users match your filters.</strong></p>
                <button className="link-btn" onClick={() => { setSearchTerm(''); setRoleFilter('all'); setStatusFilter('all'); }}>Clear filters</button>
              </>
            ) : (
              <>
                <p><strong>No users yet.</strong></p>
                <p>Add your first user to get started.</p>
                <button className="add-user-btn inline" onClick={() => setShowAddModal(true)}><Plus size={16} /> Add User</button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Add / Edit modals + detail + password + generated reveal + confirm */}
      {showAddModal && <AddUserModal {...{ newUser, setNewUser, handleAddUser, addValid, loading, error, onClose: () => setShowAddModal(false) }} />}
      {editingUser && <EditUserModal {...{ editingUser, setEditingUser, submitEdit, loading, error, isSelf: isSelf(editingUser), onClose: () => setEditingUser(null) }} />}
      {detailUser && <DetailModal user={detailUser} onClose={() => setDetailUser(null)} onEdit={() => { openEdit(detailUser); setDetailUser(null); }} />}
      {showPasswordModal && passwordChangeUser && (
        <PasswordModal {...{ user: passwordChangeUser, newPassword, setNewPassword, handleChangePassword,
          onGenerate: () => handleGeneratePassword(passwordChangeUser), loading, error,
          onClose: () => { setShowPasswordModal(false); setPasswordChangeUser(null); } }} />
      )}
      {generatedInfo && <GeneratedPasswordModal info={generatedInfo} onClose={() => setGeneratedInfo(null)} />}
      {confirm && <ConfirmDialog {...confirm} onClose={() => setConfirm(null)} />}
    </div>
  );
};

// --------------------------------------------------------------------------- //
// Sub-components (kept in-file; the app has no shared modal/dialog library)
// --------------------------------------------------------------------------- //
const Field = ({ label, help, error, children }) => (
  <div className="form-group">
    <label>{label}</label>
    {children}
    {help && !error && <p className="helper-text">{help}</p>}
    {error && <p className="form-error" role="alert">{error}</p>}
  </div>
);

const AddUserModal = ({ newUser, setNewUser, handleAddUser, addValid, loading, error, onClose }) => {
  const set = (k, v) => setNewUser((p) => ({ ...p, [k]: v }));
  const emailInvalid = newUser.email && !/\S+@\S+\.\S+/.test(newUser.email);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head"><h2>Add New User</h2><button className="modal-x" onClick={onClose} aria-label="Close"><X size={18} /></button></div>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handleAddUser} noValidate>
          <h3 className="form-section">Identity</h3>
          <div className="form-row">
            <Field label="First Name"><input type="text" value={newUser.firstName} onChange={(e) => set('firstName', e.target.value)} required /></Field>
            <Field label="Last Name"><input type="text" value={newUser.lastName} onChange={(e) => set('lastName', e.target.value)} required /></Field>
          </div>
          <Field label="Email" help="This will be the user's login ID." error={emailInvalid ? 'Enter a valid email address.' : ''}>
            <input type="email" value={newUser.email} onChange={(e) => set('email', e.target.value)} required />
          </Field>
          <Field label="Role">
            <select value={newUser.role} onChange={(e) => set('role', e.target.value)}>
              <option value="user">User</option><option value="Admin">Admin</option>
            </select>
          </Field>

          <h3 className="form-section">Password Setup</h3>
          <div className="form-fieldset">
            <label className="radio-row"><input type="radio" name="pwmode" checked={newUser.passwordMode === 'generate'} onChange={() => set('passwordMode', 'generate')} /> System-generated temporary password</label>
            <label className="radio-row"><input type="radio" name="pwmode" checked={newUser.passwordMode === 'manual'} onChange={() => set('passwordMode', 'manual')} /> Set a password manually</label>
            {newUser.passwordMode === 'manual' && (
              <Field label="Password" error={newUser.password && newUser.password.length < 8 ? 'At least 8 characters.' : ''}>
                <input type="text" value={newUser.password} minLength={8} onChange={(e) => set('password', e.target.value)} placeholder="Min 8 characters" />
              </Field>
            )}
            <label className="checkbox-row"><input type="checkbox" checked={newUser.mustChangePassword} disabled={newUser.passwordMode === 'generate'} onChange={(e) => set('mustChangePassword', e.target.checked)} /> Require password change on first login</label>
          </div>

          <h3 className="form-section">Access Control</h3>
          <div className="form-row">
            <Field label="Access start"><input type="datetime-local" value={newUser.accessStartAt} onChange={(e) => set('accessStartAt', e.target.value)} /></Field>
            <Field label="Access end"><input type="datetime-local" value={newUser.accessEndAt} onChange={(e) => set('accessEndAt', e.target.value)} /></Field>
          </div>
          <p className="helper-text section-help">User can sign in only during this date range.</p>

          <h3 className="form-section">Usage Limits</h3>
          <div className="form-fieldset">
            <label className="radio-row"><input type="radio" name="qlmode" checked={newUser.queryLimitType === 'unlimited'} onChange={() => set('queryLimitType', 'unlimited')} /> Unlimited</label>
            <label className="radio-row"><input type="radio" name="qlmode" checked={newUser.queryLimitType === 'limited'} onChange={() => set('queryLimitType', 'limited')} /> Limited to
              <input type="number" min={0} className="inline-number" value={newUser.queryLimit} disabled={newUser.queryLimitType !== 'limited'} onChange={(e) => set('queryLimit', e.target.value)} /> queries</label>
            <p className="helper-text">Set a maximum number of queries or allow unlimited access.</p>
          </div>

          <Field label="Notes (internal)"><textarea rows={2} value={newUser.notes} onChange={(e) => set('notes', e.target.value)} /></Field>

          <div className="modal-actions">
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={loading || !addValid}>{loading ? 'Creating…' : 'Create User'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditUserModal = ({ editingUser, setEditingUser, submitEdit, loading, error, isSelf, onClose }) => {
  const set = (k, v) => setEditingUser((p) => ({ ...p, [k]: v }));
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head"><h2>Edit User</h2><button className="modal-x" onClick={onClose} aria-label="Close"><X size={18} /></button></div>
        {isSelf && <div className="self-warning"><AlertTriangle size={15} /> You are editing your own account.</div>}
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={submitEdit} noValidate>
          <h3 className="form-section">Identity</h3>
          <Field label="Email" help="Login ID cannot be changed."><input type="email" value={editingUser.email} readOnly className="readonly-input" /></Field>
          <div className="form-row">
            <Field label="First Name"><input type="text" value={editingUser.first_name || ''} onChange={(e) => set('first_name', e.target.value)} required /></Field>
            <Field label="Last Name"><input type="text" value={editingUser.last_name || ''} onChange={(e) => set('last_name', e.target.value)} required /></Field>
          </div>
          <Field label="Role"><select value={editingUser.role} onChange={(e) => set('role', e.target.value)}><option value="user">User</option><option value="Admin">Admin</option></select></Field>

          <h3 className="form-section">Access Control</h3>
          <div className="form-row">
            <Field label="Access start"><input type="datetime-local" value={editingUser.accessStartAt || ''} onChange={(e) => set('accessStartAt', e.target.value)} /></Field>
            <Field label="Access end"><input type="datetime-local" value={editingUser.accessEndAt || ''} onChange={(e) => set('accessEndAt', e.target.value)} /></Field>
          </div>

          <h3 className="form-section">Usage Limits</h3>
          <div className="form-fieldset">
            <label className="radio-row"><input type="radio" name="eqlmode" checked={editingUser.query_limit_type === 'unlimited'} onChange={() => set('query_limit_type', 'unlimited')} /> Unlimited</label>
            <label className="radio-row"><input type="radio" name="eqlmode" checked={editingUser.query_limit_type === 'limited'} onChange={() => set('query_limit_type', 'limited')} /> Limited to
              <input type="number" min={0} className="inline-number" value={editingUser.query_limit || 0} disabled={editingUser.query_limit_type !== 'limited'} onChange={(e) => set('query_limit', e.target.value)} /> queries
              <span className="used-hint"> · used {editingUser.queries_used ?? 0}</span></label>
          </div>

          <Field label="Notes (internal)"><textarea rows={2} value={editingUser.notes || ''} onChange={(e) => set('notes', e.target.value)} /></Field>

          <div className="modal-actions">
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={loading}>{loading ? 'Updating…' : 'Update User'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DetailModal = ({ user, onClose, onEdit }) => {
  const admin = isAdminRole(user.role);
  const limited = user.query_limit_type === 'limited';
  const exhausted = limited && (user.queries_used ?? 0) >= user.query_limit;
  const st = statusOf(user);
  const reason = st === 'suspended' ? 'This account has been suspended by an administrator.'
    : st === 'expired' ? 'Access ended on ' + fmtDate(user.access_end_at) + '.'
    : st === 'pending' ? 'Access starts on ' + fmtDate(user.access_start_at) + '.' : null;
  const Row = ({ label, value }) => (<div className="detail-row"><span className="detail-label">{label}</span><span className="detail-value">{value}</span></div>);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head"><h2>User Details</h2><button className="modal-x" onClick={onClose} aria-label="Close"><X size={18} /></button></div>
        <div className="detail-grid">
          <Row label="Full name" value={`${user.first_name || ''} ${user.last_name || ''}`.trim() || '—'} />
          <Row label="Email" value={user.email} />
          <Row label="Role" value={<span className={`role-badge ${admin ? 'admin' : 'user'}`}>{admin ? 'ADMIN' : 'USER'}</span>} />
          <Row label="Status" value={<StatusBadge status={st} />} />
          {reason && <div className="detail-reason"><AlertTriangle size={14} /> {reason}</div>}
          <Row label="Access start" value={fmtDateTime(user.access_start_at)} />
          <Row label="Access end" value={fmtDateTime(user.access_end_at)} />
          <Row label="Query limit" value={limited ? `Limited (${user.query_limit})` : 'Unlimited'} />
          <Row label="Queries used" value={<span className={exhausted ? 'usage-exhausted' : ''}>{user.queries_used ?? 0}{exhausted ? ' — limit reached' : ''}</span>} />
          <Row label="Created" value={fmtDateTime(user.created_at)} />
          <Row label="Last login" value={fmtDateTime(user.last_login)} />
          {user.created_by && <Row label="Created by" value={user.created_by} />}
          {user.updated_by && <Row label="Updated by" value={user.updated_by} />}
          {user.notes && <Row label="Notes" value={user.notes} />}
        </div>
        <div className="modal-actions">
          <button type="button" onClick={onClose}>Close</button>
          <button type="button" onClick={onEdit}>Edit</button>
        </div>
      </div>
    </div>
  );
};

const PasswordModal = ({ user, newPassword, setNewPassword, handleChangePassword, onGenerate, loading, error, onClose }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
      <div className="modal-head"><h2>Reset Password</h2><button className="modal-x" onClick={onClose} aria-label="Close"><X size={18} /></button></div>
      <p>Reset password for <strong>{user.email}</strong></p>
      {error && <div className="alert alert-error">{error}</div>}
      <form onSubmit={handleChangePassword} noValidate>
        <Field label="New Password" help="User will be required to change it on next login.">
          <input type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 8 characters" minLength={8} />
        </Field>
        <div className="modal-actions">
          <button type="button" onClick={onGenerate} disabled={loading}>Generate temporary instead</button>
          <button type="submit" disabled={loading || newPassword.length < 8}>{loading ? 'Saving…' : 'Set Password'}</button>
        </div>
      </form>
    </div>
  </div>
);

const GeneratedPasswordModal = ({ info, onClose }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(info.password); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head"><h2>{info.title || 'Temporary password'}</h2></div>
        <p>Share this one-time password securely with <strong>{info.email}</strong>. It will <strong>not</strong> be shown again.</p>
        <div className="gen-password-box">
          <code className="gen-password">{info.password}</code>
          <button type="button" className="copy-btn" onClick={copy}><Copy size={15} /> {copied ? 'Copied' : 'Copy'}</button>
        </div>
        {info.emailSent && <p className="helper-text">A welcome email with these details was also sent to the user.</p>}
        <div className="modal-actions"><button type="button" onClick={onClose}>Done</button></div>
      </div>
    </div>
  );
};

const ConfirmDialog = ({ title, message, confirmLabel = 'Confirm', danger, onConfirm, onClose }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal-content confirm" onClick={(e) => e.stopPropagation()}>
      <div className="modal-head"><h2>{danger && <AlertTriangle size={18} className="confirm-icon" />}{title}</h2></div>
      <p>{message}</p>
      <div className="modal-actions">
        <button type="button" onClick={onClose}>Cancel</button>
        <button type="button" className={danger ? 'btn-danger' : ''} onClick={() => { onConfirm(); onClose(); }}>{confirmLabel}</button>
      </div>
    </div>
  </div>
);

export default UserManagement;
